import React from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import ExcelForm from './ExcelForm'
import WinrestForm2 from './WinrestForm2'
import { MongoSites, getSlugFromString } from '../../utils/mongo_utils'
import { LoaderConsumer } from '../../utils/LoadingContext'

const DATA_SOURCES = {
    none: { name: 'Choose', form: () => null },
    excel: { name: 'Excel', form: ExcelForm },
    winrest2: { name: 'Winrest', form: WinrestForm2 },
    //winrest: { name: 'Winrest (old)', form: WinrestForm },
}

const FONT_COLOR = '#edeff0'

const MenuSettingsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
    position: relative;
    min-width: 350px;
    padding: 15px;
    z-index: 1;

    height: 100%;
    max-height: 100vh;
    overflow: auto;

    background-color: rgba(34, 49, 58, 0.93);
    color: ${FONT_COLOR || '#000'};

    box-shadow: 0 15px 35px rgba(50, 50, 93, 0.1),
        0 5px 15px rgba(0, 0, 0, 0.07);

    label {
        display: inline-block;
        font-size: 0.75rem;
        text-transform: uppercase;
        margin-top: 4px;
        color: ${FONT_COLOR || '#000'};
    }

    @media print {
        display: none;
    }

    transform: translateX(-99%);
    transition: transform 0.3s ease-in-out;

    ${({ settingsDrawerOpen }) =>
        !settingsDrawerOpen &&
        css`
            :hover {
                transform: translateX(-97%);
            }
        `};

    ${({ settingsDrawerOpen }) =>
        settingsDrawerOpen &&
        css`
            transform: translate(0);
        `};

    @media (max-width: 960px) {
        width: 100%;
    }
`

const Logo = styled.img`
    width: 100px;
    margin-bottom: 20px;
`

export const FormGroup = styled.div`
    margin-bottom: 0.6rem;
    display: grid;
    grid-template-columns: 100px 1fr;
    input,
    select {
        display: block;

        width: 100%;
        font-size: 0.8rem;
        line-height: 1.25;
        color: ${FONT_COLOR};
        background-color: #2b3942;
        border-top: 0;
        border-right: 0;
        border-bottom: 1px solid ${FONT_COLOR};
        border-left: 0;
        border-radius: 5px;
        padding: 3px;
        outline: 0;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        -webkit-transition-delay: 99999s;
    }

    select {
        color: ${FONT_COLOR};
        background: #2b3942;
    }
    small {
        font-size: 0.7rem;
        font-weight: bold;
        opacity: 0.7;
        margin-top: 5px;
    }
`

export const KioskSelection = styled.div`
    justify-items: right;
    margin-bottom: 15px;
    ${({ nogrid }) =>
        nogrid
            ? `
    input {
        margin-right: 10px;
    }
    `
            : `display: grid;
            grid-template-columns: 1fr 1fr;`}
`

export const ActionLink = styled.div`
    text-align: center;
    margin-top: 25px;
    a,
    div {
        color: #fff;
        font-size: 0.9em;
        font-weight: 600;
        text-transform: uppercase;
        text-decoration: none;
    }

    div:hover {
        cursor: pointer;
    }

    @media print {
        display: none;
    }
`

export const Separator = styled.div`
    margin: 10px 35px;
`

const MenuSettings = props => {
    const {
        entityId,
        siteId,
        file,
        title,
        subtitle,
        concept,
        forceToday,
        date,
        winrestURL,
        language,
        service,
        recipes,
        menu,
        kiosks,
        displayMode,
        dayCount,
        orientation,
        zoom,
        size,
        startDay,
        settingsDrawerOpen,
        openDrawer,
        closeDrawer,
        dataSource,
        mealTypeSlug,
        pointOfSaleSlug,
        handleChange,
        menuSlug,
        guestTypeSlug,
        useCommercialName,
        hideMenuPrice,
        hideRecipePoints,
        hideEnergyKcal,
        showEnergyKcalByPortion,
        hideComposition,
        hideLabels,
        hideIntolerences,
        isDuoKiosk,
        print,
        showDate,
        excludedDays,
    } = props
    const generateUrl = () => {
        let queryParams = `
            ?dataSource=${dataSource}&
            ${title ? `t=${encodeURIComponent(title)}&` : ''}
            ${subtitle ? `st=${encodeURIComponent(subtitle)}&` : ''}
            hideMenuPrice=${hideMenuPrice || false}&
            hideRecipePoints=${hideRecipePoints || false}&
            hideEnergyKcal=${hideEnergyKcal || false}&
            showEnergyKcalByPortion=${showEnergyKcalByPortion || false}&
            hideComposition=${hideComposition || false}&
            hideLabels=${hideLabels || false}&
            hideIntolerences=${hideIntolerences || false}&
            isDuoKiosk=${isDuoKiosk || false}&
            mkt=${concept}&
            date=${forceToday ? 'today' : date}&
            l=${language}&
            mode=${
                print
                    ? displayMode === 'week'
                        ? 'week'
                        : 'print'
                    : displayMode
            }&
            dayCount=${dayCount}&
            orientation=${orientation}&
            ${print ? `print=1&` : ''}
            zoom=${zoom}&
            size=${size}&
            showDate=${showDate || false}&
            startDay=${startDay}&
            excludedDays=${excludedDays.toString()}
        `

        if (dataSource === 'excel') {
            const excelQueryParams = `
                entityId=${entityId}&
                siteId=${siteId}&
                f=${file}&
                s=${service}&
                ${menu ? 'menu=' + menu + '&' : ''}
                kiosks=${kiosks
                    .reduce((result, item) => {
                        if (item.enabled) {
                            result.push(
                                `${item.name}-${
                                    item.kioskPicture
                                        ? item.kioskPicture
                                        : item.name
                                }`
                            )
                        }
                        return result
                    }, [])
                    .join(',')}
            `
            return `${queryParams}&${excelQueryParams}`.replace(/$\n^\s*/gm, '')
        } else if (dataSource === 'winrest') {
            const currentSite = MongoSites.findOne({ id: Number(siteId) }) || {}
            const winrestQueryParams = `
                siteSlug=${getSlugFromString(currentSite.name)}&
                url=${winrestURL}&
                useCommercialName=${useCommercialName ? true : false}&
                pointOfSaleSlug=${pointOfSaleSlug}&
                mealTypeSlug=${mealTypeSlug}&
                menuSlug=${menuSlug}&
                guestTypeSlug=${guestTypeSlug}&
                recipes=${recipes}
            `
            return `${queryParams}&${winrestQueryParams}`.replace(
                /$\n^\s*/gm,
                ''
            )
        } else if (dataSource === 'winrest2') {
            const { winrestData } = props
            if (!winrestData) {
                return null
            }
            const {
                baseUrl,
                siteId,
                pointOfSaleId,
                guestTypeId,
                mealTypeId,
                menuId,
                selectedRecipeTypes,
            } = winrestData
            if (!siteId || !pointOfSaleId) {
                return null
            }

            const pictures = JSON.stringify(winrestData.pictures[concept])

            const winrestQueryParams = `
                url=${baseUrl}&
                siteId=${siteId}&
                pointOfSaleId=${pointOfSaleId}&
                guestTypeId=${guestTypeId}&
                mealTypeId=${mealTypeId}&
                menuId=${menuId}&
                recipeTypeIds=${encodeURIComponent(selectedRecipeTypes.join())}&
                pictures=${encodeURIComponent(pictures || '')}&
                useCommercialName=${useCommercialName ? true : false}
        `
            return `${queryParams}&${winrestQueryParams}`.replace(
                /$\n^\s*/gm,
                ''
            )
        }
    }

    const FormWithSource = DATA_SOURCES[dataSource]
        ? DATA_SOURCES[dataSource].form
        : () => null

    return (
        <MenuSettingsWrapper
            settingsDrawerOpen={settingsDrawerOpen}
            onClick={openDrawer}
            id="settings-panel"
        >
            <Logo src="img/newrest_logo_w.png" />
            <FormGroup>
                <label>Data source:</label>

                <select
                    name="dataSource"
                    value={dataSource}
                    onChange={handleChange}
                >
                    {Object.keys(DATA_SOURCES).map((source, index) => (
                        <option value={source} key={index}>
                            {DATA_SOURCES[source].name}
                        </option>
                    ))}
                </select>
            </FormGroup>

            <FormWithSource {...props} generateUrl={generateUrl} />

            <Separator />
        </MenuSettingsWrapper>
    )
}

export default LoaderConsumer(MenuSettings)

MenuSettings.propTypes = {
    // func
    closeDrawer: PropTypes.func,
    handleChange: PropTypes.func,
    openPrintDialog: PropTypes.func,
    openDrawer: PropTypes.func,
    winrestApiFetcher: PropTypes.func,

    // boolean
    settingsDrawerOpen: PropTypes.bool,
    forceToday: PropTypes.bool,
    print: PropTypes.bool,

    // string
    entityId: PropTypes.string,
    file: PropTypes.string,
    concept: PropTypes.string,
    date: PropTypes.string,
    language: PropTypes.string,
    service: PropTypes.string,
    menu: PropTypes.string,
    displayMode: PropTypes.string,
    orientation: PropTypes.string,
    size: PropTypes.string,
    startDay: PropTypes.string,
    dataSource: PropTypes.string,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    siteId: PropTypes.string,

    // number
    zoom: PropTypes.number,
    dayCount: PropTypes.number,

    // array
    displayModes: PropTypes.array,
    kiosks: PropTypes.array,
    days: PropTypes.array,
}
